import { v4 } from 'uuid';

const LOG_PREFIX = '[CONNECTION]:';
const serverUri = process.env.REACT_APP_XMPP_SERVER_URI ?? '';

export type TConnectionType = Strophe.Connection & { mam: any };
export type TStatatusCallback = (status: Strophe.Status | undefined) => void;
export type TConnectionCallback = (connection: TConnectionType | undefined) => void;

/**
 * Interval to send keep alive packages for connection.
 * Max idle interval on server is 60s.
 */
const WSS_KEEP_ALIVE_INTERVAL = 45000;

/**
 * Service to handle strophe connection
 */
class ConnectionService {
  /**
   * Connection
   */
  private _connection: TConnectionType | undefined;
  /**
   * Status of connection
   */
  protected _connectionStatus: Strophe.Status | undefined;
  /**
   * Queue of promises waiting for connection
   */
  private connectionPromises: [
    (value: TConnectionType | PromiseLike<TConnectionType>) => void,
    (reason?: any) => void,
  ][];
  /**
   * Id of interval func when connected with wss
   */
  private keepAliveId: NodeJS.Timeout | undefined;

  /**
   * Handle connection status change notification
   */
  private connectionStatusListeners: Record<string, TStatatusCallback> = {};
  get connectionStatus() {
    return this._connectionStatus;
  }
  protected set connectionStatus(newStatus: Strophe.Status | undefined) {
    this._connectionStatus = newStatus;

    for (const key in this.connectionStatusListeners) {
      if (Object.prototype.hasOwnProperty.call(this.connectionStatusListeners, key)) {
        const listener = this.connectionStatusListeners[key];
        listener(newStatus);
      }
    }
  }

  /**
   * Handle connection instance notifications
   */
  private connectionInstanceListeners: Record<string, TConnectionCallback> = {};
  private get connection() {
    return this._connection;
  }
  private set connection(newConn: TConnectionType | undefined) {
    this._connection = newConn;

    for (const key in this.connectionInstanceListeners) {
      if (Object.prototype.hasOwnProperty.call(this.connectionInstanceListeners, key)) {
        const listener = this.connectionInstanceListeners[key];
        listener(newConn);
      }
    }
  }

  constructor() {
    this.connectionPromises = [];
  }

  /**
   * Send keep alive package when connected, or clear
   */
  private sendKeepAlive = () => {
    if (this.connection && this.connectionStatus === Strophe.Status.CONNECTED) {
      this.connection?.send($build('body').t(' '));
    } else {
      this.stopKeepAlive();
    }
  };

  /**
   * Start sending keep alive
   */
  private startKeepAlive = () => {
    this.sendKeepAlive();
    this.keepAliveId = setInterval(this.sendKeepAlive, WSS_KEEP_ALIVE_INTERVAL);
  };

  /**
   * Clear keep alive handler
   */
  private stopKeepAlive = () => {
    if (this.keepAliveId) {
      clearTimeout(this.keepAliveId);
      this.keepAliveId = undefined;
    }
  };

  /**
   * Function to resolve all promises waiting for connection
   */
  private resolveConnectionPromises = () => {
    for (const pair of this.connectionPromises) {
      pair[0](this.connection!);
    }

    this.connectionPromises = [];
  };

  private log = (message: any) => {
    // tslint:disable-next-line: no-console
    console.log(`${LOG_PREFIX} ${message}`);
  };

  /**
   * Function to create new connection
   */
  public createNewConnection = async () => {
    this.connection = new Strophe.Connection(serverUri, {
      keepalive: true,
    }) as TConnectionType;

    this.connectionStatus = undefined;

    return this.connection;
  };

  /**
   * Function to login as given user,
   * send presence to server
   * and resolve all waiting promises
   */
  public connect = (
    username: string,
    password: string,
    callback?: (status: Strophe.Status) => void,
  ) =>
    new Promise<Strophe.Status>(async (res, rej) => {
      if (username && password) {
        if (!this.connection) {
          await this.createNewConnection();
        }

        this.connection!.connect(username, password, (status: Strophe.Status) => {
          this.connectionStatus = status;
          callback?.(status);
          if (status === Strophe.Status.CONNECTED) {
            this.connection?.send($pres());
            this.resolveConnectionPromises();

            /**
             * If wss, we need to to send keep alive empty packages
             */
            if (serverUri.includes('wss')) {
              this.startKeepAlive();
            }

            res(status);
          } else if (status === Strophe.Status.ERROR) {
            this.abortConnection();
            rej(status);
          }
          return true;
        });
      } else {
        rej();
      }
    });

  /**
   * Logout and close connection
   */
  public disconnectAndDestroy = () => {
    if (this.connection) {
      this.disconnect();
    }
    this.connection = undefined;
  };

  /**
   * Logout and close connection
   */
  public disconnect = () => {
    this.stopKeepAlive();

    if (this.connection) {
      this.connection.flush();
      this.connection.disconnect('');
      this.connectionStatus = Strophe.Status.DISCONNECTED;
    }
  };

  /**
   * Function to get connection object
   */
  public getConnection = () => {
    return this.connection;
  };

  /**
   * Get status of current connection
   */
  public getStatus = () => {
    return this.connectionStatus;
  };

  /**
   * Function to wait for connection
   */
  public waitForConnection = () =>
    new Promise<TConnectionType>((res, rej) => {
      if (this.connectionStatus !== Strophe.Status.CONNECTED) {
        this.connectionPromises.push([res, rej]);
      } else {
        res(this.connection!);
      }
    });

  /**
   * Abort all connection waiting function
   */
  public abortConnection = () => {
    for (const pair of this.connectionPromises) {
      pair[1]();
    }

    this.connectionPromises = [];
  };

  /**
   * Add status listener
   * @param fn callback
   * @returns unregister fn
   */
  public addStatusListener = (fn: TStatatusCallback) => {
    const id = v4();

    this.connectionStatusListeners[id] = fn;

    return () => {
      delete this.connectionStatusListeners[id];
    };
  };

  /**
   * Add instance listener
   * @param fn callback
   * @returns unregister fn
   */
  public addConnectionListener = (fn: TConnectionCallback) => {
    const id = v4();

    this.connectionInstanceListeners[id] = fn;

    return () => {
      delete this.connectionInstanceListeners[id];
    };
  };
}

const connectionService = new ConnectionService();

export default connectionService;
